<template>
  <!-- Dropdown: one pill with the chosen finish; the list opens under it. -->
  <div v-if="dropdown" ref="root" class="finish-select" :class="{ 'is-open': open }" @keydown.esc="open = false">
    <button
      type="button"
      class="finish-select--toggle"
      aria-haspopup="listbox"
      :aria-expanded="String( open )"
      @click="open = !open"
    >
      <span class="finish-select--label">{{ label }}</span>
      <Color v-if="current" :value="[ current.color, current.color2 ]" small />
      <span class="finish-select--name">{{ name( value ) }}</span>
      <v-icon small>mdi-chevron-down</v-icon>
    </button>
    <ul v-if="open" class="finish-select--menu" role="listbox" :aria-label="label">
      <li v-for="( color, i ) in colors" :key="i">
        <button
          type="button"
          role="option"
          :aria-selected="String( i === value )"
          class="finish-select--option"
          :class="{ 'is-active': i === value }"
          @click="choose( i )"
        >
          <Color :value="[ color.color, color.color2 ]" small />
          <span>{{ name( i ) }}</span>
          <v-icon v-if="i === value" small>mdi-check</v-icon>
        </button>
      </li>
    </ul>
  </div>
  <div v-else class="part-drawer--option finish-chips">
    <span class="part-drawer--label">{{ label }}</span>
    <div class="part-drawer--chips" role="radiogroup" :aria-label="label">
      <button
        v-for="( color, i ) in colors"
        :key="i"
        type="button"
        role="radio"
        :aria-checked="String( i === value )"
        class="part-chip"
        :class="{ 'is-active': i === value }"
        @click="$emit( 'input', i )"
      >
        <Color :value="[ color.color, color.color2 ]" small />
        {{ name( i ) }}
      </button>
    </div>
  </div>
</template>

<script>
  import { Color } from '@/components';

  // A part's finishes: named chips in the phone drawer; a dropdown on desktop
  // on the preview under the view buttons. Also styles the drawer's width chips.
  export default {
    components: { Color },
    props: {
      label: { type: String, default: 'Colour' },
      // Colour rows in swatch order (the index selectedColor refers to).
      colors: { type: Array, default: () => [] },
      value: { type: Number, default: 0 },
      dropdown: { type: Boolean, default: false }
    },
    data() {
      return { open: false };
    },
    computed: {
      current() {
        return this.colors[ this.value ] || null;
      }
    },
    watch: {
      open( open ) {
        document[ open ? 'addEventListener' : 'removeEventListener' ]( 'pointerdown', this.outside, true );
      }
    },
    beforeDestroy() {
      document.removeEventListener( 'pointerdown', this.outside, true );
    },
    methods: {
      name( i ) {
        const color = this.colors[ i ];
        const text = color && color.colorName;
        return text ? text.charAt( 0 ).toUpperCase() + text.slice( 1 ) : `Colour ${ i + 1 }`;
      },
      choose( i ) {
        this.open = false;
        this.$emit( 'input', i );
      },
      outside( e ) {
        if ( this.$refs.root && !this.$refs.root.contains( e.target ) ) this.open = false;
      }
    }
  };
</script>

<style lang="scss">
  .part-drawer--option { display: flex; align-items: center; gap: 10px; margin-top: 12px; min-width: 0; }
  .part-drawer--label { flex: 0 0 auto; font-size: 12px; color: #62695e; }
  .part-drawer--chips {
    display: flex; gap: 2px; min-width: 0; padding: 3px; overflow-x: auto; scrollbar-width: none;
    border: 1px solid var(--builder-line, #dedfd7); border-radius: 999px; background: #fff;
  }
  .part-drawer--chips::-webkit-scrollbar { display: none; }
  .part-chip {
    display: inline-flex; align-items: center; gap: 6px; flex: 0 0 auto; height: 32px; padding: 0 12px;
    border-radius: 999px; font-size: 13px; white-space: nowrap; color: var(--builder-ink, #252c22);
  }
  .part-chip .b-color, .finish-select .b-color { width: 14px; height: 14px; margin: 0; border-radius: 50%; flex: none; }
  .part-chip .b-color > span, .finish-select .b-color > span { border-width: 7px; }
  .part-chip.is-active { background: var(--builder-accent, #344b35); color: #fff; }
  .part-chip.is-active .b-color { box-shadow: 0 0 0 1.5px #fff; }
  .part-chip.is-warn:not(.is-active) { color: #7c6845; }
  .part-chip:focus-visible { outline: 2px solid var(--builder-accent, #344b35); outline-offset: 2px; }

  .finish-select { position: relative; color: var(--builder-ink, #252c22); }
  .finish-select--toggle {
    display: inline-flex; align-items: center; gap: 8px; height: 40px; padding: 0 10px 0 14px; max-width: 100%;
    border: 1px solid var(--builder-line, #dedfd7); border-radius: 999px; background: rgba(255, 255, 255, .94); font-size: 13px;
  }
  .finish-select--toggle:hover { background: #fff; }
  .finish-select--label { color: #62695e; }
  .finish-select--name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .finish-select--toggle .v-icon { color: inherit; transition: transform .15s; }
  .finish-select.is-open .finish-select--toggle .v-icon { transform: rotate(180deg); }
  .finish-select--menu, .v-application .finish-select--menu {
    position: absolute; top: calc(100% + 6px); right: 0; z-index: 5; min-width: 100%; max-height: 320px; overflow-y: auto;
    margin: 0; padding: 4px; list-style: none; border: 1px solid var(--builder-line, #dedfd7); border-radius: 14px;
    background: #fff; box-shadow: 0 12px 28px rgba(37, 44, 34, .14);
  }
  .finish-select--option {
    display: flex; align-items: center; gap: 10px; width: 100%; height: 36px; padding: 0 10px;
    border-radius: 10px; font-size: 13px; white-space: nowrap; text-align: left;
  }
  .finish-select--option span { flex: 1; }
  .finish-select--option:hover, .finish-select--option:focus-visible { background: #f2f1ec; outline: none; }
  .finish-select--option.is-active { font-weight: 500; }
  .finish-select--option .v-icon { color: var(--builder-accent, #344b35); }
  .finish-select--toggle:focus-visible { outline: 2px solid var(--builder-accent, #344b35); outline-offset: 2px; }
</style>
